import React, { useCallback, useMemo, useState } from 'react';
import type { ActivityViewProps } from '../../core/types/activity.types';
import type { RhythmCanvasPayload, RhythmCanvasRuntime } from '../../core/types/payloads/rhythm_canvas.types';
import type { MelodyStaffPayload, MelodyStaffRuntime } from '../../core/types/payloads/melody_staff.types';
import type { EarTrainingPayload, EarTrainingRuntime } from '../../core/types/payloads/ear_training.types';
import { pitchToSpanishLabel } from '../../core/music/staffRender';
import { VexRhythmGlyph } from '../../core/music/vex/VexRhythmGlyph';
import { VexRhythmStrip } from '../../core/music/vex/VexRhythmStrip';
import { RhythmWorkshopGrid } from '../../core/music/vex/RhythmWorkshopGrid';
import { BeatCellWorkshopGrid, BeatCellStrip } from '../../core/music/vex/BeatCellWorkshopGrid';
import { VexStaffFragment } from '../../core/music/vex/VexStaffFragment';
import {
  frequenciesFromNoteNames,
  playFrequencySequence,
  playPatternTokens,
  playRhythmClicks,
} from '../../core/music/webAudio';
import { rhythmCanvasBeatCount } from '../rhythm_canvas/rhythm_canvas.logic';
import { tokenToLabel } from '../../core/music/tokens';

function dispatchSubmit(dispatch: ActivityViewProps['dispatch'], action: string, data?: unknown) {
  void dispatch({ type: action, payload: data });
}

function slotResponseMap(response: unknown): Record<string, string> {
  if (response && typeof response === 'object' && !Array.isArray(response)) {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(response as Record<string, unknown>)) {
      out[k] = String(v ?? '');
    }
    return out;
  }
  return {};
}

function RhythmTrackDisplay({
  payload,
  missingIndex,
}: {
  payload: Pick<
    RhythmCanvasPayload,
    | 'patternTokens'
    | 'timeSignature'
    | 'graphicMode'
    | 'rhythmMethod'
    | 'targetBarNumber'
    | 'rhythmDots'
    | 'beatCells'
    | 'scoreEvents'
  >;
  missingIndex?: number;
}) {
  return (
    <VexRhythmStrip
      pattern={
        payload.scoreEvents?.length
          ? payload.scoreEvents.map((e) => e.token)
          : payload.beatCells?.length
            ? payload.beatCells.flatMap((cell) => cell.tokens)
            : payload.patternTokens
      }
      beatCells={payload.scoreEvents?.length ? undefined : payload.beatCells}
      scoreEvents={payload.scoreEvents}
      timeSignature={payload.timeSignature}
      missingIndex={missingIndex}
      showLabels={payload.graphicMode === 'symbols-labels'}
      readingMethod={payload.rhythmMethod}
      targetBarNumber={payload.targetBarNumber}
      dottedIndices={payload.rhythmDots}
      showClef
    />
  );
}

// ── rhythm_canvas ──
export function RhythmCanvasStudent({
  payload,
  runtime,
  dispatch,
  participantId,
}: ActivityViewProps<'rhythm_canvas'>) {
  const rt = runtime as RhythmCanvasRuntime;
  const value = participantId ? rt.responses[participantId] : undefined;
  const slotValues = useMemo(() => slotResponseMap(value), [value]);
  const finalized = participantId ? rt.finalized[participantId] : false;
  const feedback = participantId ? rt.slotFeedback?.[participantId] : undefined;
  const score = participantId ? rt.scores?.[participantId] : undefined;
  const [playing, setPlaying] = useState(false);
  const beats = rhythmCanvasBeatCount(payload);

  const blankIndices =
    payload.blankIndices?.length
      ? payload.blankIndices
      : payload.taskKind === 'complete' && payload.missingIndex != null
        ? [payload.missingIndex]
        : (payload.beatCells ?? payload.patternTokens).map((_, i) => i);

  const usesWorkshop =
    payload.taskKind === 'complete' ||
    payload.taskKind === 'transcribe' ||
    payload.taskKind === 'practice' ||
    payload.taskKind === 'syllables';

  const useBeatCells = Boolean(payload.beatCells?.length);

  const playMetronome = useCallback(async () => {
    if (playing) return;
    setPlaying(true);
    try {
      if (payload.taskKind === 'practice' || payload.taskKind === 'transcribe') {
        await playPatternTokens(payload.patternTokens, payload.metronomeBpm ?? 72);
      } else {
        await playRhythmClicks(Math.max(1, Math.round(beats)), payload.metronomeBpm ?? 72);
      }
    } finally {
      setPlaying(false);
    }
  }, [playing, beats, payload]);

  const onSlotChange = useCallback(
    (index: number, val: string) => {
      dispatchSubmit(dispatch, 'FILL', { slotIndex: index, value: val });
    },
    [dispatch],
  );

  return (
    <div className="eval-block eval-block--rhythm-canvas">
      <h2 className="eval-block__title">{payload.title}</h2>
      <p className="eval-block__body">{payload.prompt}</p>

      {(payload.taskKind === 'practice' ||
        payload.taskKind === 'transcribe' ||
        payload.taskKind === 'complete') && (
        <button type="button" className="sala-btn-hero mb-4 text-sm" disabled={playing} onClick={playMetronome}>
          {playing ? '▶ Reproduciendo…' : payload.taskKind === 'practice' ? '▶ Escuchar patrón' : '▶ Metrónomo'}
        </button>
      )}

      {usesWorkshop ? (
        useBeatCells ? (
          <BeatCellWorkshopGrid
            beatCells={payload.beatCells!}
            timeSignature={payload.timeSignature}
            readingMethod={payload.rhythmMethod ?? 'takadimi'}
            taskKind={payload.taskKind === 'practice' ? 'transcribe' : payload.taskKind}
            blankIndices={blankIndices}
            missingIndex={payload.missingIndex}
            dottedIndices={payload.rhythmDots}
            targetBarNumber={payload.targetBarNumber}
            graphicMode={payload.graphicMode}
            hidePattern={payload.hidePattern}
            patternTokens={payload.patternTokens}
            values={slotValues}
            feedback={feedback}
            disabled={!!finalized}
            onSlotChange={onSlotChange}
            cellOptions={payload.cellOptions}
          />
        ) : (
          <RhythmWorkshopGrid
            pattern={payload.patternTokens}
            timeSignature={payload.timeSignature}
            readingMethod={payload.rhythmMethod}
            taskKind={payload.taskKind === 'practice' ? 'transcribe' : payload.taskKind}
            blankIndices={blankIndices}
            missingIndex={payload.missingIndex}
            dottedIndices={payload.rhythmDots}
            targetBarNumber={payload.targetBarNumber}
            graphicMode={payload.graphicMode}
            hidePattern={payload.hidePattern}
            values={slotValues}
            feedback={feedback}
            disabled={!!finalized}
            onSlotChange={onSlotChange}
          />
        )
      ) : payload.taskKind === 'identify' &&
        (payload.cellOptions?.length || payload.options?.length) ? (
        <>
          <RhythmTrackDisplay payload={payload} missingIndex={payload.missingIndex} />
          <ul className="music-option-grid">
            {(payload.cellOptions?.length
              ? payload.cellOptions
              : payload.options!.map((tok, i) => ({
                  id: String(i),
                  label: tokenToLabel(tok, payload.rhythmMethod),
                  token: tok,
                }))
            ).map((opt, i) => (
              <li key={opt.id ?? i}>
                <button
                  type="button"
                  className={`music-option-tile${value === i ? ' music-option-tile--active' : ''}`}
                  disabled={finalized}
                  onClick={() => dispatchSubmit(dispatch, 'FILL', { value: i })}
                >
                  <VexRhythmGlyph token={opt.token} size={36} color="#e8e4dc" />
                  <span className="text-xs">{opt.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : payload.taskKind === 'match' && payload.matchPairs?.length ? (
        <div className="print-vincula-grid music-match-grid">
          <div className="print-vincula-col print-vincula-col--a">
            <p className="print-vincula-head">Figuras</p>
            <ul className="music-match-list">
              {payload.matchPairs.map((pair) => (
                <li key={pair.id} className="music-match-row">
                  <VexRhythmGlyph token={pair.token} size={40} color="#e8e4dc" />
                </li>
              ))}
            </ul>
          </div>
          <div className="print-vincula-col print-vincula-col--b">
            <p className="print-vincula-head">Nombres</p>
            <ul className="music-match-list">
              {[...payload.matchPairs]
                .sort((a, b) => a.label.localeCompare(b.label, 'es'))
                .map((pair) => (
                  <li key={`l-${pair.id}`} className="music-match-row">
                    <span>{pair.label}</span>
                  </li>
                ))}
            </ul>
          </div>
        </div>
      ) : (
        <>
          <RhythmTrackDisplay payload={payload} missingIndex={payload.missingIndex} />
          <input
            className="sala-input-dark w-full text-sm"
            disabled={finalized}
            value={String(value ?? '')}
            placeholder="Tu respuesta…"
            onChange={(e) => dispatchSubmit(dispatch, 'FILL', { value: e.target.value })}
          />
        </>
      )}

      {finalized && typeof score === 'number' && (
        <p className="rhythm-workshop__score mt-3 text-sm text-emerald-400">
          Resultado: {score} / {blankIndices.length} aciertos
          {rt.aggregates.correct != null ? ` (${rt.aggregates.correct}%)` : ''}
        </p>
      )}

      {!finalized && (
        <button type="button" className="sala-btn-hero mt-4 text-xs" onClick={() => dispatchSubmit(dispatch, 'SUBMIT')}>
          Enviar
        </button>
      )}
    </div>
  );
}

export function RhythmCanvasHost({ payload, runtime }: ActivityViewProps<'rhythm_canvas'>) {
  const rt = runtime as RhythmCanvasRuntime | undefined;
  const submitted = rt?.aggregates?.submitted ?? 0;
  const avgCorrect = rt?.aggregates?.correct;

  return (
    <div className="eval-block eval-block--host">
      <h2 className="eval-block__title">{payload.title}</h2>
      {payload.beatCells?.length ? (
        <BeatCellStrip
          beatCells={payload.beatCells}
          timeSignature={payload.timeSignature}
          targetBarNumber={payload.targetBarNumber}
        />
      ) : (
        <RhythmTrackDisplay payload={payload} missingIndex={payload.missingIndex} />
      )}
      {payload.taskKind === 'syllables' && (
        <p className="eval-block__hint mt-2">
          Sílabas:{' '}
          {(payload.beatCells ?? []).length
            ? payload.beatCells!.map((c) => c.takadimi).join(' · ')
            : payload.patternTokens.map((t) => tokenToLabel(t, payload.rhythmMethod)).join(' · ')}
        </p>
      )}
      {submitted > 0 && (
        <p className="eval-block__hint mt-2">
          Enviados: {submitted}
          {avgCorrect != null ? ` · Promedio aciertos: ${avgCorrect}%` : ''}
        </p>
      )}
    </div>
  );
}

export function RhythmCanvasProjector(props: ActivityViewProps<'rhythm_canvas'>) {
  return <RhythmCanvasHost {...props} />;
}

// ── melody_staff ──
export function MelodyStaffStudent({
  payload,
  runtime,
  dispatch,
  participantId,
}: ActivityViewProps<'melody_staff'>) {
  const rt = runtime as MelodyStaffRuntime;
  const value = participantId ? rt.responses[participantId] ?? '' : '';
  const finalized = participantId ? rt.finalized[participantId] : false;

  return (
    <div className="eval-block eval-block--melody-staff">
      <h2 className="eval-block__title">{payload.title}</h2>
      <p className="eval-block__body">{payload.prompt}</p>
      <VexStaffFragment
        notes={payload.notes}
        clef={payload.clef}
        timeSignature={payload.timeSignature}
        highlightNoteId={payload.highlightNoteId}
        density={payload.density ?? (payload.notes.length <= 4 ? 'compact' : 'comfortable')}
        className="music-staff-frame"
      />
      <label className="mt-4 block text-sm text-slate-300">
        Nota:
        <input
          className="sala-input-dark mt-1 w-full text-sm"
          disabled={finalized}
          value={value}
          placeholder="Ej: Sol"
          onChange={(e) => dispatchSubmit(dispatch, 'FILL', { value: e.target.value })}
        />
      </label>
      {!finalized && (
        <button type="button" className="sala-btn-hero mt-4 text-xs" onClick={() => dispatchSubmit(dispatch, 'SUBMIT')}>
          Enviar
        </button>
      )}
    </div>
  );
}

export function MelodyStaffHost({ payload }: ActivityViewProps<'melody_staff'>) {
  const answer = payload.answerLabel || pitchToSpanishLabel(payload.notes.find((n) => n.id === payload.highlightNoteId)?.pitch ?? 'G4');
  return (
    <div className="eval-block eval-block--host">
      <h2 className="eval-block__title">{payload.title}</h2>
      <VexStaffFragment
        notes={payload.notes}
        clef={payload.clef}
        timeSignature={payload.timeSignature}
        highlightNoteId={payload.highlightNoteId}
        density={payload.density ?? (payload.notes.length <= 4 ? 'compact' : 'comfortable')}
        className="music-staff-frame"
      />
      <p className="eval-block__hint mt-2">Respuesta esperada: {answer}</p>
    </div>
  );
}

export function MelodyStaffProjector(props: ActivityViewProps<'melody_staff'>) {
  return <MelodyStaffHost {...props} />;
}

// ── ear_training ──
export function EarTrainingStudent({
  payload,
  runtime,
  dispatch,
  participantId,
}: ActivityViewProps<'ear_training'>) {
  const rt = runtime as EarTrainingRuntime;
  const value = participantId ? rt.responses[participantId] ?? '' : '';
  const finalized = participantId ? rt.finalized[participantId] : false;
  const [playing, setPlaying] = useState(false);
  const tokens = payload.patternTokens ?? [];

  const playAudio = useCallback(async () => {
    if (playing) return;
    setPlaying(true);
    try {
      if (payload.frequencies?.length) {
        await playFrequencySequence(payload.frequencies);
      } else if (payload.noteNames?.length) {
        await playFrequencySequence(frequenciesFromNoteNames(payload.noteNames));
      } else if (tokens.length) {
        await playPatternTokens(tokens, 72);
      }
    } finally {
      setPlaying(false);
    }
  }, [playing, payload, tokens]);

  const slotValues = slotResponseMap(value);
  const onSlotChange = useCallback(
    (index: number, val: string) => {
      dispatchSubmit(dispatch, 'FILL', { slotIndex: index, value: val });
    },
    [dispatch],
  );

  return (
    <div className="eval-block eval-block--ear-training">
      <h2 className="eval-block__title">{payload.title}</h2>
      <p className="eval-block__body">{payload.studentPrompt}</p>
      <button type="button" className="sala-btn-hero mb-4 text-sm" disabled={playing} onClick={playAudio}>
        {playing ? '▶ Reproduciendo…' : '▶ Escuchar'}
      </button>

      {payload.kind === 'rhythm' && tokens.length > 0 ? (
        <RhythmWorkshopGrid
          pattern={tokens}
          timeSignature={payload.rhythmPattern?.timeSignature ?? '4/4'}
          readingMethod={payload.rhythmMethod}
          taskKind="transcribe"
          blankIndices={tokens.map((_, i) => i)}
          graphicMode={payload.graphicMode}
          hidePattern
          values={slotValues}
          disabled={!!finalized}
          onSlotChange={onSlotChange}
        />
      ) : payload.options?.length ? (
        <ul className="music-option-grid music-option-grid--text">
          {payload.options.map((opt, i) => (
            <li key={i}>
              <button
                type="button"
                className={`music-option-tile music-option-tile--text${value === String(i) ? ' music-option-tile--active' : ''}`}
                disabled={finalized}
                onClick={() => dispatchSubmit(dispatch, 'FILL', { value: i })}
              >
                {opt}
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <input
          className="sala-input-dark w-full text-sm"
          disabled={finalized}
          value={String(value)}
          placeholder="Tu respuesta…"
          onChange={(e) => dispatchSubmit(dispatch, 'FILL', { value: e.target.value })}
        />
      )}

      {!finalized && (
        <button type="button" className="sala-btn-hero mt-4 text-xs" onClick={() => dispatchSubmit(dispatch, 'SUBMIT')}>
          Enviar
        </button>
      )}
    </div>
  );
}

export function EarTrainingHost({ payload }: ActivityViewProps<'ear_training'>) {
  return (
    <div className="eval-block eval-block--host">
      <h2 className="eval-block__title">{payload.title}</h2>
      <div className="print-teacher-guide music-ear-guide">
        <p className="print-teacher-guide__label">🗣️ Dictado docente</p>
        <p className="print-teacher-guide__body">{payload.teacherScript}</p>
      </div>
      {payload.patternTokens?.length ? (
        <RhythmTrackDisplay
          payload={{
            patternTokens: payload.patternTokens,
            timeSignature: payload.rhythmPattern?.timeSignature ?? '4/4',
            rhythmMethod: payload.rhythmMethod,
            graphicMode: payload.graphicMode,
          }}
        />
      ) : null}
    </div>
  );
}

export function EarTrainingProjector(props: ActivityViewProps<'ear_training'>) {
  return <EarTrainingHost {...props} />;
}
